import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Input, Button, Alert, Typography, message } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { clientProfileApi } from '../../../api/clientProfileApi';
import { useHaptic } from '../../../hooks/useHaptic';
import type { UserMeUpdateResponseDto } from '../../../types/auth';

interface Props {
  open: boolean;
  initialName: string | null;
  tenantId: number;
  onSuccess: (user: UserMeUpdateResponseDto) => void;
  onClose: () => void;
}

interface BeFieldError {
  field: string;
  code: string;
}

interface BeErrorShape {
  response?: {
    status?: number;
    data?: {
      code?: string;
      error?: { fields?: BeFieldError[] };
    };
  };
  code?: string;
}

export const EditNameModal: React.FC<Props> = ({
  open,
  initialName,
  tenantId,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation('customer');
  const haptic = useHaptic();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = z.object({
    firstName: z
      .string()
      .trim()
      .min(1, t('profile.editName.errorBlank'))
      .max(100, t('profile.editName.errorTooLong')),
  });

  type FormValues = z.infer<typeof schema>;

  const mapFieldErrorCode = (code: string): string => {
    switch (code) {
      case 'NOTBLANK':
      case 'BLANK_NOT_ALLOWED':
        return t('profile.editName.errorBlank');
      case 'SIZE':
        return t('profile.editName.errorTooLong');
      case 'PATTERN':
        return t('profile.editName.errorPattern');
      default:
        return t('profile.editName.errorInvalidValue');
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: initialName ?? '' },
  });

  useEffect(() => {
    if (open) {
      reset({ firstName: initialName ?? '' });
      setSubmitError(null);
    }
  }, [open, initialName, reset]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setSubmitError(null);
      const trimmed = values.firstName.trim();
      try {
        const updated = await clientProfileApi.updateMe(tenantId, { firstName: trimmed });
        haptic.notifySuccess();
        void message.success(t('profile.editName.successMessage'));
        onSuccess(updated);
      } catch (err) {
        const axErr = err as BeErrorShape;
        const status = axErr.response?.status;
        const fields = axErr.response?.data?.error?.fields;
        const isOffline = !navigator.onLine || axErr.code === 'ERR_NETWORK';

        if (status === 400 && fields && fields.length > 0) {
          let mappedFirstName = false;
          for (const f of fields) {
            if (f.field === 'firstName') {
              setError('firstName', { message: mapFieldErrorCode(f.code) });
              mappedFirstName = true;
            }
          }
          if (!mappedFirstName) {
            setSubmitError(t('profile.editName.errorInvalidRetry'));
          }
          haptic.notifyError();
          return;
        }

        haptic.notifyError();
        if (isOffline) {
          setSubmitError(t('profile.editName.errorOffline'));
        } else {
          setSubmitError(t('profile.editName.errorGeneric'));
        }
      }
    },
    [tenantId, haptic, onSuccess, setError, t],
  );

  const handleCancel = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  return (
    <Modal
      title={t('profile.editName.title')}
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      maskClosable={!isSubmitting}
      closable={!isSubmitting}
      footer={null}
      centered
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {submitError && (
          <Alert
            type="error"
            message={submitError}
            style={{ marginBottom: 12 }}
            showIcon
          />
        )}

        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          {t('profile.editName.firstNameLabel')}
        </Typography.Text>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              placeholder={t('profile.editName.firstNamePlaceholder')}
              maxLength={100}
              autoFocus
              disabled={isSubmitting}
              status={errors.firstName ? 'error' : undefined}
              size="large"
            />
          )}
        />
        {errors.firstName && (
          <Typography.Text
            type="danger"
            style={{ fontSize: 12, display: 'block', marginTop: 6 }}
          >
            {errors.firstName.message}
          </Typography.Text>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <Button
            block
            onClick={handleCancel}
            disabled={isSubmitting}
            size="large"
          >
            {t('profile.editName.cancel')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isSubmitting}
            disabled={isSubmitting}
            size="large"
          >
            {t('profile.editName.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
